<template>
  <section class="post-main-section" :key="postKey" :style="highlightOnly ? { maxHeight: '80vh', overflowY: 'auto', borderRadius: 'var(--feedSocial-borderRadius)' } : {}">
    <!-- USER AND EDIT -->
    <div class="post-top">
      <img class="post-avatar" :src="post.avatar" />
      <div class="post-user-and-time">
        <div class="post-user">
          {{ post.user }}
        </div>
        <div class="post-group-and-time">
          <i class="group-name" v-if="activityName">{{ activityName }}</i>
          <div class="post-time">{{ `${dayjs(post.created_at).format('DD')}/${dayjs(post.created_at).format('MM')}/${dayjs(post.created_at).format('YY')} - ${dayjs(post.created_at).format('HH:mm')}` }}</div>
        </div>
        <!-- TRANSLATE BUTTON -->
        <div class="translate-button-container">
          <button
            class="translate-button"
            @click="toggleTranslation(post)"
            :disabled="post.translating"
            v-if="post.language && post.language !== $store.state.user.language && !useAsWidget && post.message && post.message.trim() !== '' && post.message.trim() !== '\n'">
            <ion-icon :name="post.showTranslation && post.translatedMessage ? 'language' : 'language-outline'"></ion-icon>
            {{ getTranslateButtonText(post) }}
          </button>
        </div>
      </div>

      <!-- USER OVERLAY -->
      <div class="post-user-overlay-container" v-if="post.user_id != $store.state.user.id">
        <img class="overlay-avatar" :src="post.avatar" />
        <div class="overlay-items-container">
          <div class="post-user-overlay-item">{{ post.user }}</div>
          <button class="button post-user-msg-btn" @click.prevent="openMessageChannel(post.user_id)">{{ $t('post.sendMessage') }}</button>
        </div>
      </div>
      <ion-icon class="pinned-icon" name="pin-sharp" v-if="post.highlight"></ion-icon>
      <ion-icon class="post-edit-icon" name="ellipsis-horizontal" tabindex="0" @focus="post.showEditPanel = true" v-if="!highlightOnly && editActionAuthorized(currentEditActions[0], post) && !useAsWidget"></ion-icon>
      <!-- EDIT PANEL -->
      <div class="post-edit-panel" v-focus tabindex="0" @focusout.capture="post.showEditPanel = false" v-if="post.showEditPanel">
        <div class="post-edit-action" v-for="(editAction, key) in currentEditActions" :key="key" v-show="editActionAuthorized(editAction, post)">
          <ion-icon class="post-actions-icon" :name="editAction.icon"></ion-icon>
          <div class="edit-action-container" v-html="editAction.action" @click="editHandler(post, editAction)"></div>
        </div>
      </div>
    </div>

    <!-- CONTENTS -->
    <div class="post-middle">
      <span class="post-middle-text" v-if="!post.readmoreActive">
        <div style="white-space: pre-line" v-html="trimTextHandler(post.showTranslation && post.translatedMessage ? post.translatedMessage : post.message)"></div>
        <a class="post-readmore-button" v-if="post.message && post.message.length > 600 && !post.readmoreActive" @click.prevent="post.readmoreActive = true" href="#"></a>
      </span>
      <span style="white-space: pre-line" class="post-middle-text" v-if="post.readmoreActive" v-html="post.showTranslation && post.translatedMessage ? post.translatedMessage : post.message"></span>

      <!-- PARSED LINK IMAGE AND TITLE -->
      <div class="parsed-box-media-container" v-if="post.media && post.media.parsedLink">
        <a :href="post.media.parsedLink" target="_blank">
          <img class="parsed-box-image" :src="post.media.parsedImage" />
          <div class="parsed-box-container">
            <p class="parsed-site">{{ post.media.parsedSiteName }}</p>
            <p class="parsed-title">{{ post.media.parsedTitle }}</p>
          </div>
        </a>
      </div>

      <!-- VIDEO DISPLAY -->
      <iframe class="post-video" :src="post.media.video" frameborder="0" allow="encrypted-media;" allowfullscreen v-if="post.media && post.media.video && isVideoYoutube(post.media.video)"></iframe>
      <video class="post-video" v-if="post.media && post.media.video && !isVideoYoutube(post.media.video)" controls preload="auto" webkit-playsinline playsinline>
        <source :src="post.media.video" type="video/mp4" />
      </video>

      <!-- IMAGE DISPLAY -->
      <media-display class="media-display-container" :autoPlay="false" :playSpeed="10000" :wheelControl="false" :mouseDrag="false" :trimWhiteSpace="true" v-if="post.media && post.media.images">
        <media-slide v-for="(image, key) in post.media.images" :key="key">
          <img class="post-image" :src="image" @click="imageDisplayHandler(post.media.images)" />
        </media-slide>
        <media-pagination slot="hooper-addons" v-if="post.media && post.media.images && post.media.images.length > 1"></media-pagination>
      </media-display>
      <!-- IMAGE FULL DISPLAY -->
      <div class="post-image-panel" v-if="showImageFull" tabindex="0" @keydown.esc="showImageFull = false">
        <span class="post-image-fullscreen-close-btn" @click.prevent="showImageFull = !showImageFull"><ion-icon name="close-circle"></ion-icon></span>
        <media-display class="post-image-fullscreen-gallery" v-focus>
          <input type="text" />
          <media-slide v-for="(element, key) in imageFull" :key="key">
            <img class="post-image-fullscreen" :src="element" />
          </media-slide>
          <media-pagination slot="hooper-addons"></media-pagination>
        </media-display>
      </div>
    </div>

    <!-- BUTTONS AND COMMENTS -->
    <div class="post-bottom">
      <div class="post-bottom-reactions">
        <div class="post-bottom-reactions-values" v-if="post.like > 0">
          <!-- ❤  -->
          <HeartAnimationBtn :iconColor="'#fff'" ref="heartIcon" />
        </div>
        <div v-if="!post.like"></div>
        <div class="post-bottom-reactions-values" @click.prevent="openComments(post)" v-if="post.commentaries > 0">{{ post.commentaries }} {{ post.commentaries === 1 ? $t('post.comments') : $t('post.commentsPlural') }}</div>
      </div>

      <div class="post-bottom-actions" v-if="!useAsWidget">
        <div class="post-bottom-separator"></div>
        <div class="post-bottom-action-btn" @click="handleLike(post)">
          <span style="display: flex; align-items: center" v-if="!post.i_reacted">
            <ion-icon class="post-actions-icon" name="heart-outline"></ion-icon>
            {{ $t('post.like') }}
          </span>
          <span style="display: flex; align-items: center" v-if="post.i_reacted">
            <ion-icon class="post-actions-icon" name="thumbs-down-outline"></ion-icon>
            {{ $t('post.unlike') }}
          </span>
        </div>
        <div class="post-bottom-action-btn" @click.prevent="openComments(post)" v-if="isAudiencePostingAllowed || userIsAdmin">
          <span style="display: flex; align-items: center">
            <ion-icon class="post-actions-icon" name="chatbox-outline"></ion-icon>
            {{ $t('post.comment') }}
          </span>
        </div>
      </div>

      <!-- COMMENT INPUT -->
      <div class="post-comments-container" v-if="post.showComment">
        <div class="post-comment-input-container" v-if="isAudiencePostingAllowed || userIsAdmin">
          <img class="post-input-avatar" :src="user.picture" />
          <input class="comment-input-textbox" :placeholder="$t('post.commentPlaceholder')" v-model="enteredComment" @keypress.enter="sendComment(post, enteredComment)" v-focus />
          <div class="comments-emoji-picker-wrapper">
            <emoji-picker @emoji="append" :search="search">
              <div class="comments-emoji-invoker" slot="emoji-invoker" slot-scope="{ events: { click: clickEvent } }" @click.stop="clickEvent">
                <svg height="24" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M0 0h24v24H0z" fill="none" />
                  <path
                    d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm3.5-9c.83 0 1.5-.67 1.5-1.5S16.33 8 15.5 8 14 8.67 14 9.5s.67 1.5 1.5 1.5zm-7 0c.83 0 1.5-.67 1.5-1.5S9.33 8 8.5 8 7 8.67 7 9.5 7.67 11 8.5 11zm3.5 6.5c2.33 0 4.31-1.46 5.11-3.5H6.89c.8 2.04 2.78 3.5 5.11 3.5z" />
                </svg>
              </div>
              <div slot="emoji-picker" slot-scope="{ emojis, insert }">
                <div class="comment-emoji-picker">
                  <div class="emoji-picker__search">
                    <input type="text" v-model="search" v-focus />
                  </div>
                  <div>
                    <div v-for="(emojiGroup, category) in emojis" :key="category">
                      <h5>{{ category }}</h5>
                      <div class="emojis">
                        <span v-for="(emoji, emojiName) in emojiGroup" :key="emojiName" @click="insert(emoji)" :title="emojiName">{{ emoji }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </emoji-picker>
          </div>
          <ion-icon style="top: 32px; right: 10px" class="comments-emoji-invoker" name="image-outline" @click.stop="insertImageOnComment(post.comment_id, enteredComment, post)"></ion-icon>
        </div>

        <!-- COMMENTS -->
        <div class="post-comments-comment" v-for="comment in post.comments || []" :key="comment.comment_id + commentsKey">
          <div class="comment-avatar">
            <img :src="comment.avatar" />
          </div>
          <!-- USER OVERLAY -->
          <div class="comment-user-overlay-container" v-if="comment.user_id != $store.state.user.id">
            <img class="overlay-avatar" :src="comment.avatar" />
            <div class="overlay-items-container">
              <div class="post-user-overlay-item type-name">{{ comment.user }}</div>
              <!-- <div class="post-user-overlay-item type-company" v-if="comment.company"><ion-icon name="business-outline"></ion-icon>{{comment.company}}</div> -->
              <!-- <div class="post-user-overlay-item type-role"><ion-icon name="briefcase-outline"></ion-icon>{{comment.role}}</div> -->
              <!-- <a class="post-user-overlay-item type-email" :href="'mailto:'+comment.email"><ion-icon name="mail-outline"></ion-icon>{{comment.email}}</a> -->
              <!-- <div class="post-user-overlay-item" v-if="comment.about_me"><ion-icon name="person-outline"></ion-icon>{{comment.about_me}}</div> -->
              <button class="button post-user-msg-btn" @click.prevent="openMessageChannel(comment.user_id)">{{ $t('post.sendMessage') }}</button>
            </div>
          </div>

          <div class="comment-texts">
            <div class="comment-user">
              <strong>{{ comment.user }}</strong>
            </div>
            <div class="comment-time">{{ `${dayjs(comment.created_at).format('DD')} ${dayjs(comment.created_at).format('MMMM')} - ${dayjs(comment.created_at).format('HH:mm')}` }}</div>

            <!-- TRANSLATE BUTTON FOR COMMENT -->
            <div class="translate-button-container" v-if="comment.language && comment.language !== $store.state.user.language">
              <button class="translate-button" @click="toggleTranslation(comment)" :disabled="comment.translating">
                <ion-icon :name="comment.showTranslation && comment.translatedMessage ? 'language' : 'language-outline'"></ion-icon>
                {{ getTranslateButtonText(comment) }}
              </button>
            </div>

            <ion-icon
              class="comment-edit-icon"
              name="ellipsis-horizontal"
              tabindex="0"
              @focus="
                comment.showEditPanel = true;
                commentsKey = $RNG.randomKey();
              "
              v-if="editActionAuthorized(currentEditActions[0], comment)"></ion-icon>

            <!-- COMMENT EDIT PANEL -->
            <div
              class="post-edit-panel isComment"
              v-focus
              tabindex="0"
              @focusout.capture="
                comment.showEditPanel = false;
                commentsKey = $RNG.randomKey();
              "
              v-if="editActionAuthorized(currentEditActions[0], comment) && comment.showEditPanel">
              <div class="post-edit-action" v-for="(editAction, key) in currentEditActions" :key="key" v-show="editActionAuthorized(editAction, comment) && !editAction.needsAuthorization">
                <ion-icon class="post-actions-icon" :name="editAction.icon"></ion-icon>
                <div
                  class="edit-action-container"
                  v-html="editAction.action"
                  @click.prevent="
                    editHandler(comment, editAction);
                    comment.edit = false;
                  "></div>
              </div>
            </div>

            <div class="comment-content" v-html="comment.showTranslation && comment.translatedMessage ? comment.translatedMessage : comment.message"></div>
            <!-- PARSED LINK IMAGE AND TITLE -->
            <div class="comment-parsed-box-media-container" v-if="comment.media.parsedLink">
              <a :href="comment.media.parsedLink" target="_blank">
                <img class="post-image" :src="comment.media.parsedImage" />
                <div class="parsed-box-container">
                  <p class="parsed-site">{{ comment.media.parsedSiteName }}</p>
                  <p class="parsed-title">{{ comment.media.parsedTitle }}</p>
                </div>
              </a>
            </div>
            <!-- IMAGE DISPLAY -->
            <img class="post-image" :src="comment.media.images[0]" v-if="comment.media.images" />
            <!-- COMMENT LIKE -->
            <div class="comment-like-container">
              <div
                class="comment-like-icon"
                :style="[comment.like > 0 ? { color: 'var(--feedSocial-textColor)', backgroundColor: 'var(--feedSocial-bgColor)' } : { color: 'var(--feedSocial-bgColor)', backgroundColor: 'var(--feedSocial-subColor)' }]"
                @click.prevent="likeComment(comment.comment_id, post)">
                <ion-icon name="heart"></ion-icon>
              </div>
              <p class="comment-like-text" v-if="comment.like > 0">{{ $t('post.liked') }}</p>
              <!-- <p class="comment-like-text" v-if="comment.like>0">{{comment.like}} curtida</p><p v-if="comment.like>1">s</p> -->
            </div>
          </div>
        </div>
        <div style="cursor: pointer" @click="loadMoreComments(post, currentLastCommentId)" v-if="evaluateLoadMore(post)">{{ $t('post.seeMoreComments') }}</div>
      </div>
    </div>
  </section>
</template>

<script>
import * as dayjs from 'dayjs';
import api from '@/services/api';
import { Hooper as MediaDisplay, Slide as MediaSlide, Navigation as MediaNavigation, Pagination as MediaPagination } from 'hooper';
import 'hooper/dist/hooper.css';
import DefaultAvatar from '@/assets/img/userSocialIconGeneric.png';
import EmojiPicker from 'vue-emoji-picker';
import VideoJSPlayer from '@/components/general/VideoJSPlayer.vue';
import HeartAnimationBtn from '@/components/ui/HeartAnimation.vue';
export default {
  name: 'Post',

  components: { MediaDisplay, MediaSlide, MediaNavigation, MediaPagination, EmojiPicker, VideoJSPlayer, HeartAnimationBtn },

  directives: {
    focus: {
      inserted: function (el) {
        el.focus();
      },
    },
  },

  props: {
    post: {
      type: Object,
      required: true,
    },
    activityName: {
      type: String,
      required: true,
    },
    userIsAdmin: {
      type: Boolean,
      required: true,
    },
    highlightOnly: {
      type: Boolean,
      required: false,
      default: false,
    },
    isAudiencePostingAllowed: {
      type: Boolean,
      required: false,
      default: true,
    },
    useAsWidget: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  data() {
    return {
      user: null,
      busy: false,
      readMoreActivated: false,
      postKey: null,
      postComposerKey: null,
      commentsKey: null,
      showPostComposer: false,
      dayjs: null,
      editablePostId: null,
      editableFatherId: undefined,
      editablePost: null,
      currentLastId: '',
      currentLastCommentId: '',
      media: { images: [] },
      editActions: [],
      editActionsHighlight: [],
      showImageFull: false,
      imageFull: null,
      enteredComment: '',
      posts: [],
      emojiIconFill: 'white',
      message: '',
      input: '',
      search: '',
      authorizationData: {},
    };
  },

  computed: {
    editActionsComputed() {
      return [
        { action: this.$t('post.actions.edit'), icon: 'create-outline', method: 'edit', needsAuthorization: false },
        { action: this.$t('post.actions.delete'), icon: 'trash-outline', method: 'delete', needsAuthorization: false },
        { action: this.$t('post.actions.pin'), icon: 'attach-outline', method: 'fix', needsAuthorization: true },
        { action: this.$t('post.actions.unpin'), icon: 'browsers-outline', method: 'unfix', needsAuthorization: true },
      ];
    },
    editActionsHighlightComputed() {
      return [{ action: this.$t('post.actions.unpin'), icon: 'browsers-outline', method: 'unfix', needsAuthorization: true }];
    },
    currentEditActions() {
      return this.highlightOnly ? this.editActionsHighlightComputed : this.editActionsComputed;
    },
  },

  beforeMount() {
    this.postKey = this.$RNG.randomKey();
    this.commentsKey = this.$RNG.randomKey();
    this.postComposerKey = this.$RNG.randomKey();
    this.dayjs = dayjs;
    this.user = this.$store.state.user;
    this.chat = this.$store.state.template.chat;
    this.emojiIconFill = this.chat.inputTextColor;
    this.userIsAdmin = this.$store.state.user.role == '1819bea8-7b46-11ea-bc55-0242ac130003' || this.$store.state.user.role == '1819bd5e-7b46-11ea-bc55-0242ac130003';
  },

  methods: {
    loadMoreComments(post, lastId) {
      let comm = `event/${this.$store.state.eventId}/comment/${post.comment_id}/comments`;
      if (lastId) {
        comm += `?last=${lastId}`;
      }
      api
        .get(comm)
        .then((response) => {
          let postsRead = response.data;
          if (postsRead) {
            // Ensure post.comments is initialized as an array
            if (!post.comments || !Array.isArray(post.comments)) {
              post.comments = [];
            }

            postsRead.map((item) => {
              // Initialize translation reactive properties
              this.$set(item, 'translating', false);
              this.$set(item, 'showTranslation', false);
              this.$set(item, 'translatedMessage', null);
              try {
                item.media = JSON.parse(item.media) ?? {};
              } catch (error) {
                item.media = {};
              }
              try {
                // item.obj = JSON.parse(item.tags);
                item.obj = JSON.parse(item.reaction);
                item.like = item.obj.like;
              } catch (error) {
                item.like = 0;
              }
              item.readmoreActive = false;
              item.showComment = false;
              item.showImage = item.media && Array.isArray(item.media) ? item.media.length > 0 : false;
              item.edit = false;
              item.avatar = item.avatar ? item.avatar : DefaultAvatar;
              post.comments.push(item);
            });

            if (post.comments.length > 0) {
              let lastPost = post.comments.slice(-1)[0];
              this.currentLastId = lastPost.comment_id;
              this.currentLastCommentId = lastPost.comment_id;
            }

            this.commentsKey = this.$RNG.randomKey();
          }
        })
        .catch((err, data) => {
          console.error('error', err.response, data);
        });
    },
    async translatePost(post) {
      if (!post.message || !this.$store.state.user.language) {
        return;
      }

      this.$set(post, 'translating', true);

      const isComment = post.father_id !== undefined && post.father_id !== null;
      if (isComment) {
        this.commentsKey = this.$RNG.randomKey();
      }

      try {
        const response = await fetch(`https://translate.blitzar.com.br/translate?text=${encodeURIComponent(post.message)}&language=${this.$store.state.user.language}`, {
          headers: {
            authorization: '50eeb973-327c-4f1e-8b97-c941e014c525',
          },
        });

        if (response.ok) {
          const translationResult = await response.json();
          this.$set(post, 'translatedMessage', translationResult.result.text);
          this.$set(post, 'showTranslation', true);
        } else {
          console.error('Translation failed:', response.statusText);
          alert(this.$t('post.errors.translationError') || 'Falha na tradução');
        }
      } catch (error) {
        console.error('Translation error:', error);
        alert(this.$t('post.errors.translationError') || 'Falha na tradução');
      } finally {
        this.$set(post, 'translating', false);
        if (isComment) {
          this.commentsKey = this.$RNG.randomKey();
        }
      }
    },
    toggleTranslation(post) {
      const isComment = post.father_id !== undefined && post.father_id !== null;

      if (post.showTranslation && post.translatedMessage) {
        this.$set(post, 'showTranslation', false);
        if (isComment) {
          this.commentsKey = this.$RNG.randomKey();
        }
      } else {
        this.translatePost(post);
      }
    },
    getTranslateButtonText(post) {
      if (post.translating) {
        return this.$t('post.translating');
      } else if (post.showTranslation && post.translatedMessage) {
        return this.$t('post.showOriginal');
      } else {
        return this.$t('post.translate');
      }
    },
    append(emoji) {
      this.enteredComment += emoji;
    },
    insert(emoji) {
      this.input += emoji;
    },
    handleLike(post) {
      // if (post.liked) {
      //   post.like--;
      //   post.liked = false;
      //   // this.dislikePost(postId);
      // } else if (!post.liked) {
      //post.like++;
      //post.liked = true;
      //console.log('Post.like= ', post.like);
      // }
      if (post.i_reacted) {
        post.like = (post.like ? post.like : 0) - 1;
        // console.log('DisLikes: ', post.like);
        post.i_reacted = false;
        this.dislikePost(post.comment_id);
      } else {
        post.like = (post.like ? post.like : 0) + 1;
        // console.log('Likes: ', post.like);
        post.i_reacted = true;
        this.likePost(post.comment_id);
        if (this.$refs.heartIcon) {
          this.$refs.heartIcon.like();
        }
      }
    },
    likePost(postId) {
      if (postId) {
        api
          .post(`event/${this.$store.state.eventId}/comment/${postId}/like`, {
            user_id: this.$store.state.user.id,
          })
          .then((response) => {})
          .catch((err, data) => {
            console.error('error', err.response, data);
            alert(this.$t('post.errors.likeError'));
          });
      }
    },
    dislikePost(postId) {
      if (postId) {
        api
          .post(`event/${this.$store.state.eventId}/comment/${postId}/dislike`, {
            user_id: this.$store.state.user.id,
          })
          .then((response) => {
            // if (post.comment_id == postId && post.liked) {
            //   post.like--;
            //   post.liked = false;
            // }
          })
          .catch((err, data) => {
            console.error('error', err.response, data);
            alert(this.$t('post.errors.unlikeError'));
          });
      }
    },
    fixPost(postId) {
      this.$emit('fixPost', postId);
    },
    unfixPost(postId) {
      this.$emit('unfixPost', postId);
    },
    sendComment(post, comment) {
      if (post.comment_id) {
        api
          .post(`event/${this.$store.state.eventId}/comment`, {
            activity_id: this.activityID,
            comment_room_id: this.activityID,
            user_id: this.$store.state.user.id,
            father_id: post.comment_id,
            message: comment,
            tags: JSON.stringify({ like: 0 }),
            highlight: false,
            media: JSON.stringify(this.media),
          })
          .then((response) => {
            this.$nextTick(() => {
              this.enteredComment = '';
              this.getComments(post);
            });
          })
          .catch((err, data) => {
            console.error('error', err.response, data);
            alert(this.$t('post.errors.commentError'));
          });
      }
    },
    openComments(post) {
      // console.log('openComments', post);
      this.enteredComment = '';

      // Get number of comments
      let singlePost = `event/${this.$store.state.eventId}/comment/${post.comment_id}`;
      api
        .get(singlePost)
        .then((response) => {
          post.commentaries = response.data[0].commentaries;
        })
        .catch((err, data) => {
          console.error('error', err.response, data);
          alert(this.$t('post.errors.readCommentsError'));
        });

      post.comments = [];

      let comm = `event/${this.$store.state.eventId}/comment/${post.comment_id}/comments?limit=20`;
      api
        .get(comm)
        .then((response) => {
          // console.log('getComments response', response.data);
          post.comments = response.data;
          post.comments.map((comment) => {
            comment.edit = false;
            // Initialize translation reactive properties
            this.$set(comment, 'translating', false);
            this.$set(comment, 'showTranslation', false);
            this.$set(comment, 'translatedMessage', null);
            try {
              comment.media = JSON.parse(comment.media) ?? {};
            } catch (error) {
              comment.media = {};
            }
            try {
              comment.obj = JSON.parse(comment.reactions);
              const reaction = comment.obj.map(({ reaction, reaction_count }) => ({ [reaction]: reaction_count }));
              reaction.map((info) => Object.assign(comment, info));
            } catch (error) {
              comment.like = 0;
            }
            try {
              comment.obj = JSON.parse(comment.my_reactions);
              const my_reaction = comment.obj.map(({ reaction, reaction_count }) => ({ [reaction]: reaction_count }));
              comment.my_reaction = my_reaction;
              comment.i_reacted = comment.my_reactions_count > 0;
            } catch (error) {
              comment.i_reated = false;
            }
          });
          if (post.commentaries > 20 && post.comments && post.comments.length > 0) {
            let lastComment = post.comments.slice(-1)[0];
            this.currentLastCommentId = lastComment.comment_id;
          }
          //Updates comments view
          post.showComment = !post.showComment;
          this.commentsKey = this.$RNG.randomKey();
          if (post.highlight) this.postKey = this.$RNG.randomKey();
          // console.log('post.showComment', post.showComment);
          this.$emit('openComments', post);
        })
        .catch((err, data) => {
          console.error('error', err.response, data);
          alert('Falha ao tentar ler postagens.');
        });
    },
    getComments(post, lastCommentId) {
      let comm = `event/${this.$store.state.eventId}/comment/${post.comment_id}/comments?limit=100000`;
      if (lastCommentId) {
        comm += `?last=${lastCommentId}`;
      }
      api
        .get(comm)
        .then((response) => {
          post.comments = response.data;
          post.comments.map((comment) => {
            comment.edit = false;
            // Initialize translation reactive properties
            this.$set(comment, 'translating', false);
            this.$set(comment, 'showTranslation', false);
            this.$set(comment, 'translatedMessage', null);
            comment.avatar = comment.avatar ? comment.avatar : DefaultAvatar;
            try {
              comment.media = JSON.parse(comment.media) ?? {};
            } catch (error) {
              comment.media = {};
            }
            try {
              comment.obj = JSON.parse(comment.reactions);
              const reaction = comment.obj.map(({ reaction, reaction_count }) => ({ [reaction]: reaction_count }));
              reaction.map((info) => Object.assign(comment, info));
            } catch (error) {
              comment.like = 0;
            }
            try {
              comment.obj = JSON.parse(comment.my_reactions);
              const my_reaction = comment.obj.map(({ reaction, reaction_count }) => ({ [reaction]: reaction_count }));
              comment.my_reaction = my_reaction;
              comment.i_reacted = comment.my_reactions_count > 0;
            } catch (error) {
              comment.i_reated = false;
            }
          });
          this.enteredComment = ' ';
          this.commentsKey = this.$RNG.randomKey();
          this.$nextTick(() => {
            this.enteredComment = '';
          });
        })
        .catch((err, data) => {
          console.error('error', err.response, data);
          alert('Falha ao carregar comentários.');
        });
    },
    likeComment(commentId, post) {
      if (commentId && post.comments && Array.isArray(post.comments)) {
        post.comments.map((comment) => {
          if (comment.comment_id == commentId) {
            this.handleLike(comment);
            this.commentsKey = this.randomKey();
          }
        });
      }
      // if (commentId) {

      //   api
      //     .post(`event/${this.$store.state.eventId}/comment/${commentId}/like`, {
      //       user_id: this.$store.state.user.id,
      //     })
      //     .then(response => {
      //       post.comments.map(comment => {
      //         if (comment.comment_id == commentId) {
      //           comment.like++;
      //           this.commentsKey = this.randomKey();
      //         }
      //       });
      //     })
      //     .catch((err, data) => {
      //       console.error('error', err.response, data);
      //       alert('Falha ao dar like.');
      //     });
      // }
    },
    deletePost(postId) {
      if (postId) {
        api
          .delete(`event/${this.$store.state.eventId}/comment/${postId}`)
          .then((response) => {
            this.$emit('postDeleted', postId);
            api.post(`sessionAction/`, {
              action: 'Delete Post',
              properties: JSON.stringify({ route: this.$route.name, params: this.$route.params, post_id: postId }),
              userid: this.$store.state.user.id,
              sessionid: this.$store.state.user.token,
              event_id: this.$store.state.eventId,
            });
          })
          .catch((err, data) => {
            console.error('error', err.response, data);
            alert('Falha ao tentar deletar post.');
          });
      }
    },
    activateReadMore() {
      this.readMoreActivated = true;
    },
    insertImageOnComment(fatherID, enteredComment, post) {
      this.editablePost = {};
      this.editablePost.media = { images: [''] };
      this.editableFatherId = fatherID;
      this.editablePostId = null;
      this.showPostComposer = true;
      this.postComposerKey = this.$RNG.randomKey();
      this.commentsKey = this.$RNG.randomKey();
      this.$emit('editPost', this.editablePost);
    },
    editHandler(post, action) {
      if (action.method == 'fix' && (this.$store.state.user.id == post.user_id || this.userIsAdmin)) {
        this.fixPost(post.comment_id);
      }
      if (action.method == 'unfix' && (this.$store.state.user.id == post.user_id || this.userIsAdmin)) {
        this.unfixPost(post.comment_id);
      }
      if (action.method == 'edit' && (this.$store.state.user.id == post.user_id || this.userIsAdmin)) {
        this.$emit('editPost', post);
      }
      if (action.method == 'delete' && (this.$store.state.user.id == post.user_id || this.userIsAdmin)) {
        let dialog = confirm('Deletar postagem?');
        if (dialog == true) {
          this.deletePost(post.comment_id);
        } else {
          post.showEditPanel = false;
          return;
        }
      }
      if (action.method == 'hide') {
      }

      post.showEditPanel = false;
    },
    openMessageChannel(thirdPartyId) {
      let channelID = thirdPartyId > this.$store.state.user.id ? thirdPartyId + this.$store.state.user.id : this.$store.state.user.id + thirdPartyId;
      api
        .post(`event/${this.$store.state.eventId}/createTalkChannel`, { user_id: thirdPartyId, channelName: channelID })
        .then((response) => {
          if (response.data) {
            this.$root.$emit('openChannel', response.data.talk_channel_id);
          }
        })
        .catch((err) => {
          console.error(err);
        });
    },
    randomKey() {
      return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
    },
    isVideoYoutube(src) {
      return src.includes('youtu');
    },
    editActionAuthorized(editAction, post) {
      if (this.userIsAdmin) {
        if (editAction.method == 'fix' && post.highlight) {
          return false;
        }
        if (editAction.method == 'unfix' && !post.highlight) {
          return false;
        }
        return true;
      } else {
        if (this.$store.state.user.id == post.user_id) {
          if (editAction.needsAuthorization) return false;
          else return true;
        } else {
          return false;
        }
      }
    },
    evaluateLoadMore(post) {
      if (!post.comments || !Array.isArray(post.comments)) {
        return false;
      }
      let count = 0;
      post.comments.map(() => {
        count++;
      });
      if (post.commentaries != count) {
        if (post.commentaries < 21) return false;
        if (count === post.commentaries + 1) return false;
        else return true;
      } else return false;
    },
    trimTextHandler(message) {
      if (message && message.length > 600) {
        return message.slice(0, 600) + '...' + ' <strong>Ver mais</strong>';
      } else {
        return message || '';
      }
    },
    imageDisplayHandler(images) {
      this.showImageFull = !this.showImageFull;
      this.imageFull = images;
    },
  },
};
</script>

<style lang="scss">
.translate-button-container {
  display: flex;
  .translate-button {
    padding: 6px 0 0;
    border-radius: 4px;
    color: var(--feedSocial-textColor, #333);
    opacity: 0.5;
    font-size: 10px;
    background: transparent;
    outline: none;
    border: none;
    // text-decoration: underline;

    display: flex;
    align-items: center;
    gap: 5px;

    cursor: pointer;

    ion-icon {
      font-size: 12px;
    }
  }
}
</style>
